import { KEY_RATINGS } from './archetypes';
import type { Rng } from './rng';
import type { Tunables } from './tunables';
import { RATING_KEYS, type Formation, type Player, type Position, type Ratings } from './types';

export type NumberRanges = Record<Position, readonly number[]>;

/** Gives each position a unique jersey number from its range; falls back to any free number 1 to 99. */
export function assignNumbers(rng: Rng, positions: readonly Position[], ranges: NumberRanges): number[] {
  const used = new Set<number>();
  return positions.map((pos) => {
    const [min = 1, max = 99] = ranges[pos];
    const free: number[] = [];
    for (let n = min; n <= max; n++) if (!used.has(n)) free.push(n);
    if (free.length === 0) for (let n = 1; n <= 99; n++) if (!used.has(n)) free.push(n);
    const pick = free[rng.int(0, free.length - 1)];
    if (pick === undefined) throw new Error('No jersey numbers left');
    used.add(pick);
    return pick;
  });
}

/** Archetype ratings (GDD section 8): key ratings from the team range, the rest from the off-trait range. */
export function generateRatings(rng: Rng, pos: Position, range: { min: number; max: number }, t: Tunables): Ratings {
  const keys = KEY_RATINGS[pos];
  const r = {} as Ratings;
  for (const k of RATING_KEYS) {
    r[k] = keys.includes(k) ? rng.int(range.min, range.max) : rng.int(t.ratings.offTraitMin, t.ratings.offTraitMax);
  }
  return r;
}

/** One player per formation slot, numbered and rated. Same seed, same squad. */
export function generateSquad(
  rng: Rng,
  teamId: string,
  formation: Formation,
  range: { min: number; max: number },
  t: Tunables,
): Player[] {
  const positions = formation.slots.map((s) => s.pos);
  const numbers = assignNumbers(rng, positions, t.roster.numberRanges);
  return formation.slots.map((slot, i) => ({
    id: `${teamId}-${i}`,
    number: numbers[i] ?? 0,
    pos: slot.pos,
    ratings: generateRatings(rng, slot.pos, range, t),
    stamina: 100,
  }));
}
